import { t, type StringKey } from '../strings';
import { countWithUnit } from './listViewer';

/**
 * 🔒 Every counted noun a sentence builds, with its two words in the string table
 * (T2165): "1 line", "4 lines", "0 lines". No sentence appends an s to a noun, so a
 * noun with another plural is one pair of strings and not a hunt through the code.
 */
const COUNTED_UNITS = {
  'BOM line': ['unit.bomLine.one', 'unit.bomLine.other'],
  'RFQ': ['unit.rfq.one', 'unit.rfq.other'],
  'already-released document': ['unit.releasedDocument.one', 'unit.releasedDocument.other'],
  'audit row': ['unit.auditRow.one', 'unit.auditRow.other'],
  'earlier request': ['unit.earlierRequest.one', 'unit.earlierRequest.other'],
  'exact company pair': ['unit.companyPair.one', 'unit.companyPair.other'],
  'line': ['unit.line.one', 'unit.line.other'],
  'location': ['unit.location.one', 'unit.location.other'],
  'minute': ['unit.minute.one', 'unit.minute.other'],
  'month': ['unit.month.one', 'unit.month.other'],
  'optional line': ['unit.optionalLine.one', 'unit.optionalLine.other'],
  'state rate': ['unit.stateRate.one', 'unit.stateRate.other'],
  'transaction': ['unit.transaction.one', 'unit.transaction.other'],
  'value': ['unit.value.one', 'unit.value.other'],
  'working day': ['unit.workingDay.one', 'unit.workingDay.other'],
  'hour': ['unit.hour.one', 'unit.hour.other'],
  'day': ['unit.day.one', 'unit.day.other'],
  'invoice': ['unit.invoice.one', 'unit.invoice.other'],
  // T2362: the Sales Reporting Dashboard's metric panels.
  'account': ['unit.account.one', 'unit.account.other'],
  'deal': ['unit.deal.one', 'unit.deal.other'],
  'credit note': ['unit.creditNote.one', 'unit.creditNote.other'],
  'customer payment': ['unit.customerPayment.one', 'unit.customerPayment.other'],
  'bill': ['unit.bill.one', 'unit.bill.other'],
  'vendor credit': ['unit.vendorCredit.one', 'unit.vendorCredit.other'],
  'company': ['unit.company.one', 'unit.company.other'],
  'person': ['unit.person.one', 'unit.person.other'],
  // Decision 7940: the sites an import Associate adds, counted on its row's notes line.
  'site': ['unit.site.one', 'unit.site.other'],
  // Decision 7500 (T3098): the rows an undone merge moved back, counted on the survivor's History.
  'row': ['unit.row.one', 'unit.row.other'],
  // Decision 12700 (T3820): what goes with a deleted Deal, counted in its window.
  'note': ['unit.note.one', 'unit.note.other'],
  'file': ['unit.file.one', 'unit.file.other'],
  'contact': ['unit.contact.one', 'unit.contact.other'],
} as const satisfies Record<string, readonly [StringKey, StringKey]>;

export type CountedUnit = keyof typeof COUNTED_UNITS;

/** A count and its noun through `countWithUnit`: the singular for exactly one, the plural for every other count, zero included. */
export function counted(count: number, unit: CountedUnit, figure: string = String(count)): string {
  const [one, other] = COUNTED_UNITS[unit];
  return countWithUnit(count, figure, { one: t(one), other: t(other) });
}
